import { useEffect, useState } from "react"
import { useNavigate, useSearch } from "@tanstack/react-router"
import { useEffectOnce } from "react-use"
import { z } from "zod"

import type { IntercomSettings, Workspace } from "@productlane/zero"
import { pro } from "@productlane/lib/src/browser"
import {
	AlertDialog,
	Button,
	IntegrationTemplate,
	IntercomIcon,
	Loader,
	Select,
} from "@productlane/ui"

import { MetaInfo } from "@/components/meta-info"
import { toast } from "@/components/toast"
import { UpgradeButton } from "@/components/upgrade-button"
import { env } from "@/env"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { useRequiredPlans } from "@/lib/plans"
import { useZeroWorkspace } from "@/lib/zero"
import { PlanPill } from "../plan-pill"
import { SettingItem } from "../settings-switches/setting-item"
import { SettingsContainer } from "../settings-switches/settings-container"
import { IntegrationLayout } from "./integration-layout"

export const ConnectIntercomClientQueryDataSchema = z.object({
	fromStore: z.string().optional(),
})
const IntercomHostRegions = {
	US: "US",
	EU: "EU",
	AU: "AU",
} as const

export type ConnectIntercomClientQueryData = z.infer<
	typeof ConnectIntercomClientQueryDataSchema
>

const INTERCOM_TITLE = "Intercom migrator"
const INTERCOM_DESCRIPTION =
	"Move your conversations, contacts and help center from Intercom to Productlane."

const INTERCOM_LONG_DESCRIPTION = (
	<div>
		<div className="mb-12">
			<div className="mb-1 text-lg font-semibold text-primary">Overview</div>
			<div className="mb-6 text-sm text-secondary">
				Connect Intercom to bring your history across: conversations with every
				reply and attachment, the contacts and companies behind them, and your
				help center.
			</div>
		</div>
		<div className="mb-12">
			<div className="mb-1 text-lg font-semibold text-primary">
				How it works
			</div>
			<div className="mb-6 text-sm text-secondary">
				Connecting counts what is there and shows you the numbers first. Nothing
				is written until you start the import. An imported conversation sends no
				replies and no email, so your customers hear nothing.
			</div>
		</div>
	</div>
)

const INTERCOM_CANVAS_DESCRIPTION = (
	<div className="mb-12">
		<div className="mb-1 text-lg font-semibold text-primary">
			Your Intercom widget
		</div>
		<div className="mb-6 text-sm text-secondary">
			A Productlane widget appears in Intercom for every company and person, so
			your team can push feedback and see its delivery status without leaving
			Intercom. It keeps working as it does today.
		</div>
	</div>
)

export function IntercomIntegrationsPage() {
	const workspace = useZeroWorkspace()
	return (
		<>
			{workspace ? (
				<IntercomForm data={{ workspace }} />
			) : (
				<div className="flex justify-center">
					<Loader />
				</div>
			)}
		</>
	)
}

const dataHostRegionOptions = [
	{
		value: IntercomHostRegions.US,
		label: "United States",
	},
	{
		value: IntercomHostRegions.EU,
		label: "Europe",
	},
	{
		value: IntercomHostRegions.AU,
		label: "Australia",
	},
]

interface IntercomFormProps {
	data: {
		workspace: Workspace & {
			intercomSettings?: IntercomSettings
		}
	}
}

function IntercomForm({ data }: IntercomFormProps) {
	const ctx = api.useUtils()
	const navigate = useNavigate()
	const isSuccess = useSearch({ strict: false, select: (s) => s.success })

	const [dialogOpen, setDialogOpen] = useState(false)

	const intercomQuery = api.workspace.intercomSettings.useQuery(undefined, {
		cacheTime: Infinity,
		staleTime: Infinity,
	})

	const [dataHostRegion, setDataHostRegion] = useState<
		keyof typeof IntercomHostRegions
	>(intercomQuery.data?.settings?.dataHostRegion ?? IntercomHostRegions.US)

	useEffect(() => {
		const stored = intercomQuery.data?.settings?.dataHostRegion
		if (stored) setDataHostRegion(stored)
	}, [intercomQuery.data?.settings?.dataHostRegion])

	const updateMutation = api.workspace.updateIntercom.useMutation({
		onSuccess: () => {
			toast.success({ title: "Data host region updated" })
		},
		onError: () => {
			setDataHostRegion(
				intercomQuery.data?.settings?.dataHostRegion ?? IntercomHostRegions.US,
			)
		},
	})

	useEffectOnce(() => {
		if (isSuccess === "true") {
			toast.success({
				title: "Success",
				description: "Intercom is now connected",
			})
			void navigate({ to: "/settings/integrations/intercom", replace: true })
		} else if (isSuccess === "false") {
			toast.error({
				title: "Something went wrong",
				description: "Feel free to try again or contact us.",
			})
			void navigate({ to: "/settings/integrations/intercom", replace: true })
		}
	})
	const disconnectMutation = api.workspace.disconnectIntercom.useMutation({
		onSuccess: async () => {
			await ctx.workspace.info.invalidate()
		},
	})
	const { passes: hasRequiredPlan } = useRequiredPlans(pro.andUp)
	const connected = !!data.workspace.intercomSettings
	const [isLoading, setIsLoading] = useState(false)

	return (
		<>
			<MetaInfo title="Intercom migrator" />
			<IntegrationLayout />

			<IntegrationTemplate
				name={
					<div className="flex items-center gap-3">
						{INTERCOM_TITLE} <PlanPill plan="pro" />
					</div>
				}
				description={INTERCOM_DESCRIPTION}
				logo={<IntercomIcon className="w-10" />}
				instructions={
					<div className="flex flex-col justify-center">
						{connected && (
							<div className="mb-12 space-y-4">
								<div className="text-sm font-medium">Options</div>
								<SettingsContainer>
									<SettingItem
										title="Data host region"
										description="Where Intercom stores your workspace, so we read it from the right API."
										action={
											<Select
												name="dataHostRegion"
												value={dataHostRegion}
												options={dataHostRegionOptions}
												disabled={updateMutation.isLoading}
												selectClassName="dark:bg-gray-700/70"
												onChange={(value) => {
													analytics.track("update-intercom-settings")
													setDataHostRegion(
														value as keyof typeof IntercomHostRegions,
													)
													updateMutation.mutate({
														dataHostRegion:
															value as keyof typeof IntercomHostRegions,
													})
												}}
											/>
										}
									/>
								</SettingsContainer>
							</div>
						)}
						{connected && INTERCOM_CANVAS_DESCRIPTION}
						{!connected && <>{INTERCOM_LONG_DESCRIPTION}</>}
					</div>
				}
				button={
					<div>
						{hasRequiredPlan ? (
							connected ? (
								<Button
									isLoading={disconnectMutation.isLoading}
									onClick={() => setDialogOpen(true)}
								>
									Disconnect
								</Button>
							) : (
								<>
									<Button
										isLoading={isLoading}
										onClick={() => {
											setIsLoading(true)
											analytics.track("connect-intercom")
											window.location.href = `${env.VITE_API_URL}/api/intercom/connect`
										}}
									>
										Connect
									</Button>
								</>
							)
						) : (
							<UpgradeButton
								required={["pro", "scale", "business", "enterprise"]}
							/>
						)}
					</div>
				}
			/>

			<AlertDialog
				variant="positive"
				isOpen={dialogOpen}
				onOpenChange={setDialogOpen}
				confirmText="Disconnect"
				title="Disconnect Intercom?"
				description="Your imported data stays in Productlane. The Intercom widget stops working and you can connect again at any time."
				onCancel={() => {
					setDialogOpen(false)
				}}
				onConfirm={() => {
					analytics.track("disconnect-intercom")
					disconnectMutation.mutate()
					setDialogOpen(false)
				}}
			/>
		</>
	)
}
